import * as React from 'react';
import {ReactElement, useCallback, useEffect, useMemo, useState} from 'react';
import { convert, LocalDate } from '@js-joda/core';
import { DateRange, nativeDateToLocalDateAtUtc } from '@unifocus/js-utils';
import { ViewCallbackProperties } from 'react-calendar';
import { ReactCalendar } from '../../reactcalendar';
import { ActionBar, ActionBarItemProps } from '../../actionbar';
import {HorizontalPanel} from '../../../layout/horizontalpanel';

/**
 * {@docCategory LocalDateRangePicker}
 */
export interface LocalDatePresetRange {
   /**
    * Label used to display range.
    */
   label: string;

   /**
    * Object from DateRange class including start and end date.
    */
   dateRange: DateRange;
}

/**
 * {@docCategory LocalDateRangePicker}
 */
export interface LocalDateRangePickerProps {
   /**
    * Start and end dates (range) used to set calendars.
    */
   dateRange?: DateRange;

   /**
    * Called when each of ranges is selected.
    */
   onChange: (dateRange: DateRange) => void;

   /**
    * By default calendars are placed next to each other in a row. If stacked is true,
    * then calendars are in a column where first calendar is over second.
    * @default false
    */
   stacked?: boolean;

   /**
    * To prohibit selection/navigation before set date.
    */
   minDate?: LocalDate;

   /**
    * To prohibit selection/navigation after set date.
    */
   maxDate?: LocalDate;

   /**
    * Date range which will highlight dates in range.
    */
   referenceDateRange?: DateRange;

   /**
    * To add custom options which will be shown in toolbar to quickly select defined ranges.
    * @default []
    */
   presetRanges?: LocalDatePresetRange[];

   /**
    * Additional items to add as action items.
    */
   actionItems?: ReactElement;

   /**
    * To customize cells for date.
    */
   formatCell?: (date: LocalDate) => ReactElement | null;

   /**
    * To disable cells for date.
    */
   disableCell?: (date: LocalDate) => boolean;
}

export const LocalDateRangePicker = (props: LocalDateRangePickerProps): ReactElement => {
   const containerRef = React.useRef<HTMLDivElement>(null);
   const calendarRef = React.useRef(null);
   const { startDate, endDate } = props.dateRange || { startDate: undefined, endDate: undefined };
   const {
      minDate,
      maxDate,
      referenceDateRange,
      formatCell,
      disableCell,
      stacked,
      onChange,
      presetRanges,
      actionItems
   } = props;
   const optionalProps = Object.assign(
      {},
      minDate && { minDate: convert(minDate).toDate() },
      maxDate && { maxDate: convert(maxDate).toDate() },
      referenceDateRange && {
            tileClassName: ({ date }: { date: Date }) => {

               return referenceDateRange && referenceDateRange.contains(convertDateToLocalDateAtUtc(date)) ? 'local-date-range-picker__reference-date-range'
                                                                               : '';
            }
         },
      formatCell && {
            tileContent: ({ date }: { date: Date }) => {
               return formatCell ? formatCell(convertDateToLocalDateAtUtc(date)) : null;
            }
         },
      disableCell && {
            tileDisabled: ({ date }: { date: Date }) => {
               return disableCell ? disableCell(convertDateToLocalDateAtUtc(date)) : false;
            }
         });
   const stackedClassName = stacked ? 'local-date-range-picker--stacked' : '';

   const onChangeHandler = useCallback((dates: Date | Date[]) => {
      return onChange(new DateRange(convertDateToLocalDateAtUtc((dates as Date[])[0]),
                                    convertDateToLocalDateAtUtc((dates as Date[])[1])));
   },[onChange]);

   const [ activeStartDate, setActiveStartDate ] = useState<Date | undefined>(getActiveStartDate(startDate, minDate, maxDate));

   useEffect(()=>{
      setActiveStartDate(getActiveStartDate(startDate, minDate, maxDate));
   }, [startDate, minDate, maxDate]);

   const onActiveStartDateChange = useCallback((viewCallbackProperties: ViewCallbackProperties) => {
      setActiveStartDate(viewCallbackProperties.activeStartDate);
   },[]);

   const curValue = useMemo(()=> {
      if (startDate !== undefined && endDate !== undefined) {
         return [convert(startDate).toDate(), convert(endDate).toDate()];
      }
      return undefined;
   },[startDate, endDate]);

   const popupContainerFn:()=>HTMLElement = useCallback(() => {
      if (containerRef.current === null) {
         return document.body;
      }
      return containerRef.current;
   }, [containerRef]);

   return (
      <div
         ref={containerRef}
         className={`local-date-range-picker ${stackedClassName}`}
      >
         <ReactCalendar
            ref={calendarRef}
            showDoubleView={true}
            selectRange={true}
            activeStartDate={activeStartDate}
            value={curValue}
            onChange={onChangeHandler}
            onActiveStartDateChange={onActiveStartDateChange}
            {...optionalProps}
         />
         {(presetRanges || actionItems) && (
            <HorizontalPanel horizontalFill={true}
                             padding={true}
                             gap={true}
                             align={'right'}
                             className={'local-date-range-picker__toolbar'}
                             nowrap={true}
            >
               <ActionBar
                  items={convertPresetRangesToActionBarItems(presetRanges || [],
                                                             onChange,
                                                             navigateToPresetRangeStartDate.bind(null, calendarRef))}
                  getPopupContainer={popupContainerFn}
                  menuButtonSize={'small'}
                  menuButtonAlign={'left'}
               />
               <HorizontalPanel align={'right'} gap={'small'} padding={false} horizontalFill={false} nowrap={true}>
                  {actionItems}
               </HorizontalPanel>
            </HorizontalPanel>
         )}
      </div>
   );
};

const getActiveStartDate = (startDate: LocalDate | undefined, minDate: LocalDate | undefined, maxDate: LocalDate | undefined) => {

   if (maxDate && startDate && startDate.isAfter(maxDate)) {
      return convert(maxDate.minusMonths(1).withDayOfMonth(1).atStartOfDay()).toDate();
   }

   if (minDate && (startDate === undefined || startDate.isBefore(minDate))) {
      return convert(minDate.withDayOfMonth(1).atStartOfDay()).toDate();
   }

   return startDate ? convert(startDate.withDayOfMonth(1).atStartOfDay()).toDate() : undefined;
};

const convertDateToLocalDateAtUtc = (date: Date): LocalDate =>
   nativeDateToLocalDateAtUtc(new Date(Date.UTC(date.getFullYear(),
                                                date.getMonth(),
                                                date.getDate())
   ));

const navigateToPresetRangeStartDate = (calendarRef: any,
                                        startDate: LocalDate) =>
   calendarRef.current.setActiveStartDate(convert(startDate.withDayOfMonth(1)).toDate(), 'month');

const convertPresetRangesToActionBarItems = (presetRanges: LocalDatePresetRange[],
                                             onChange: (dateRange: DateRange) => void,
                                             navigateToPresetRangeStartDate: (startDate: LocalDate) => void): ActionBarItemProps[] =>
   presetRanges.map(presetRange => ({
      name: presetRange.label,
      size: 'small',
      onClick: () => {
         navigateToPresetRangeStartDate(presetRange.dateRange.startDate);
         onChange(presetRange.dateRange);
      }
   }));
